import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorSettings } from "../../services/helper";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  Row,
  Col,
  CardBody,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  FaShoppingCart,
  FaDollarSign,
  FaCalculator,
  FaCoins,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";
import classnames from "classnames";

const RoundsReport = ({ backTo }) => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const dispatch = useDispatch();
  const [data, setData] = useState({ summary: {}, orders: [] });
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0], // Last month
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchRoundsReport();
  }, []);

  const fetchRoundsReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const { data } = await http.get(
        `${api_url}/reports/rounding?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching rounds report:", error);
    }
    setLoading(false);
  };

  const printReport = () => {
    const vendor_email = getVendorSettings("email");
    const vendor_store_name = getVendorSettings("name");
    const vendor_address = getVendorSettings("vendor_contact_address");
    const vendor_phone = getVendorSettings("vendor_contact_phone");
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Government Rounding Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .header p { margin: 0; }
            .header h3 { margin-top: 20px; margin-bottom: 10px; }
            .header hr { margin-top: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${vendor_store_name}</h2>
            <p>${vendor_address}</p>
            <p>${vendor_phone} | ${vendor_email}</p>
            <h3>Government Rounding Report</h3>
            <hr>
          </div>
          <div class="summary">
            <p><strong>Period:</strong> ${data.summary.period}</p>
            <p><strong>Total Orders:</strong> ${data.summary.total_orders}</p>
            <p><strong>Total Original Amount:</strong> $${data.summary.total_original_amount.toFixed(2)}</p>
            <p><strong>Total Rounded Amount:</strong> $${data.summary.total_rounded_amount.toFixed(2)}</p>
            <p><strong>Total Adjustment:</strong> $${data.summary.total_adjustment.toFixed(2)}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Original Amount</th>
                <th>Rounded Amount</th>
                <th>Adjustment</th>
              </tr>
            </thead>
            <tbody>
              ${data.orders.map(order => `
                <tr>
                  <td>${order.order_id}</td>
                  <td>${new Date(order.date).toLocaleDateString()}</td>
                  <td>$${order.original_amount.toFixed(2)}</td>
                  <td>$${order.rounded_amount.toFixed(2)}</td>
                  <td>$${order.govt_rounding_adjustment.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const columns = [
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    {
      name: "Date",
      selector: (row) => new Date(row.date).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Original Amount",
      selector: (row) => `$${row.original_amount.toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Rounded Amount",
      selector: (row) => `$${row.rounded_amount.toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Adjustment",
      selector: (row) => `$${row.govt_rounding_adjustment.toFixed(2)}`,
      sortable: true,
    },
  ];

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {backTo && (
            <Button color="primary" onClick={() => dispatch(openView(backTo))}>
              Back
            </Button>
          )}
          <h2 className="text-center flex-grow-1">Government Rounding Report</h2>
          <div></div>
        </div>

        {/* Filters */}
        <div className="d-flex mb-3 gap-2">
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange({ ...dateRange, start: e.target.value })
            }
          />
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange({ ...dateRange, end: e.target.value })
            }
          />
          <Button color="primary" onClick={fetchRoundsReport}>
            Filter
          </Button>
          <Button
            color="info"
            onClick={() => {
              setDateRange({
                start: new Date(new Date().setMonth(new Date().getMonth() - 1))
                  .toISOString()
                  .split("T")[0],
                end: new Date().toISOString().split("T")[0],
              });
              fetchRoundsReport();
            }}
          >
            Reset
          </Button>
        </div>

        {/* Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "summary" })}
              onClick={() => setActiveTab("summary")}
              style={{ cursor: "pointer" }}
            >
              Summary
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "listing" })}
              onClick={() => setActiveTab("listing")}
              style={{ cursor: "pointer" }}
            >
              Listing
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="summary">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <>
                <Row className="mb-4">
                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaShoppingCart
                          size={30}
                          className="text-primary mb-2"
                        />
                        <CardTitle tag="h5">Total Orders</CardTitle>
                        <h4>{data.summary.total_orders}</h4>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaDollarSign size={30} className="text-warning mb-2" />
                        <CardTitle tag="h5">Original Amount</CardTitle>
                        <h4>${data.summary.total_original_amount?.toFixed(2)}</h4>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaCoins size={30} className="text-success mb-2" />
                        <CardTitle tag="h5">Rounded Amount</CardTitle>
                        <h4>${data.summary.total_rounded_amount?.toFixed(2)}</h4>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="3">
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <FaCalculator size={30} className="text-info mb-2" />
                        <CardTitle tag="h5">Total Adjustment</CardTitle>
                        <h4>${data.summary.total_adjustment?.toFixed(2)}</h4>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>

          <TabPane tabId="listing">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <Card className="p-3 shadow-sm">
                <h4 className="mb-3">Rounding Details</h4>
                <DataTable
                  columns={columns}
                  data={data.orders}
                  pagination
                  striped
                />
              </Card>
            )}
          </TabPane>
        </TabContent>
        
        <div className="text-center mt-4 mb-4">
          <Button color="success" onClick={printReport}>
            Print Report
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default RoundsReport;